import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import ContactModal from '../components/contacts/ContactModal';
import { getContacts, getContact, deleteContact } from '../services/apiContacts';
import { createNote, deleteNote } from '../services/apiNotes';
import { resolveMediaUrl } from '../lib/utils';
import ReactMarkdown from 'react-markdown';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useSearchStore } from '../store/useSearchStore';
import { useModules } from '../lib/useModules';
import DictateButton from '../components/common/DictateButton';

dayjs.extend(relativeTime);

export default function Contacts() {
    const { searchQuery, setSearchQuery } = useSearchStore();
    const { isFeatureEnabled } = useModules();

    const canCreateContact = isFeatureEnabled('contacts', 'create');
    const canEditContact = isFeatureEnabled('contacts', 'edit');
    const canDeleteContact = isFeatureEnabled('contacts', 'delete');
    const canUseNotes = isFeatureEnabled('contacts', 'notes');

    // Clear search query on unmount
    useEffect(() => {
        return () => {
            setSearchQuery('');
        };
    }, [setSearchQuery]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [activeContactId, setActiveContactId] = useState(null);
    const [activeTab, setActiveTab] = useState('actividad');
    const [timelineFilter, setTimelineFilter] = useState('all');

    // Note states
    const [noteBody, setNoteBody] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState(null);

    const queryClient = useQueryClient();
    const fileInputRef = useRef(null);

    // Fetch basic contact list
    const { data: contactsResponse, isLoading, error, isFetching } = useQuery({
        queryKey: ['contacts', searchQuery],
        queryFn: () => getContacts(searchQuery.trim().length >= 3 ? { search: searchQuery } : {})
    });

    // Fetch full details of active contact
    const { data: activeContactDetails, isLoading: isLoadingDetails } = useQuery({
        queryKey: ['contact', activeContactId],
        queryFn: () => getContact(activeContactId),
        enabled: !!activeContactId
    });

    const deleteMutation = useMutation({
        mutationFn: deleteContact,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            setActiveContactId(null);
        }
    });

    const createNoteMutation = useMutation({
        mutationFn: createNote,
        onSuccess: () => {
            setNoteBody('');
            setAttachments([]);
            queryClient.invalidateQueries({ queryKey: ['contact', activeContactId] });
        }
    });

    const deleteNoteMutation = useMutation({
        mutationFn: deleteNote,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contact', activeContactId] });
            setIsDeleteModalOpen(false);
            setNoteToDelete(null);
        }
    });

    if (error) {
        return <div className="p-8 text-red-500 font-bold text-center">API Error: {error.message || 'Error loading contacts'}</div>;
    }

    const contacts = Array.isArray(contactsResponse?.data) ? contactsResponse.data : [];
    const activeContact = activeContactDetails || contacts.find(c => c.id === activeContactId) || contacts[0];

    const handleSaveNote = () => {
        if (!noteBody.trim() && attachments.length === 0) return;
        
        const formData = new FormData();
        formData.append('notable_type', 'contact');
        formData.append('notable_id', activeContact.id);
        formData.append('body', noteBody);
        
        attachments.forEach(file => {
            formData.append('attachments[]', file);
        });

        createNoteMutation.mutate(formData);
    };

    const handleDeleteClick = (noteId, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setNoteToDelete(noteId);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = () => {
        if (!noteToDelete) return;
        deleteNoteMutation.mutate(noteToDelete);
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setAttachments(prev => [...prev, ...Array.from(e.target.files)]);
        }
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleDeleteContact = () => {
        if (activeContact && window.confirm(`¿Estás seguro de que deseas eliminar a ${activeContact.full_name || 'este contacto'}?`)) {
            deleteMutation.mutate(activeContact.id);
        }
    };

    const handleEditContact = () => {
        if (activeContact) {
            setSelectedContact(activeContact);
            setIsModalOpen(true);
        }
    };

    const handleCreateContact = () => {
        setSelectedContact(null);
        setIsModalOpen(true);
    };

    return (
        <>
            <Header
                title="Contactos"
                total={contactsResponse?.meta?.total ? new Intl.NumberFormat('en-US').format(contactsResponse?.meta?.total) : '...'}
                actionButton={
                    <div className="flex items-center gap-2">
                        {activeContactId && (
                            <Link to={`/contacts/${activeContactId}/360`} className="xl:hidden bg-primary text-white hover:bg-primary/90 shrink-0 transition-colors shadow-sm p-1.5 rounded-md flex flex-col items-center justify-center min-w-[36px]" title="Vista 360">
                                <span className="material-symbols-outlined text-sm">360</span>
                            </Link>
                        )}
                        {canCreateContact && (
                            <button
                                onClick={handleCreateContact}
                                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-primary/90 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[20px]">add</span>
                                <span className="hidden sm:inline">Crear Contacto</span>
                            </button>
                        )}
                    </div>
                }
            />

            {/* Content Split View */}
            <div className="flex flex-1 overflow-hidden">
                {/* Contact List (Left Panel) */}
                <div className="flex flex-1 flex-col overflow-hidden bg-surface-light dark:bg-surface-dark w-full lg:w-2/3 xl:w-3/5 border-r border-gray-200 dark:border-gray-800">

                    {/* Filters Bar */}
                    <div className="flex flex-wrap items-center gap-2 p-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
                        <button className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-text-main hover:bg-gray-50 dark:border-gray-700 dark:bg-surface-dark dark:text-gray-300">
                            <span className="material-symbols-outlined text-[16px]">filter_list</span>
                            Filtros
                        </button>
                        <div className="h-4 w-px bg-gray-200 dark:bg-gray-700 mx-1"></div>
                        <button className="rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors">
                            Todos
                        </button>
                        <button className="rounded-full border border-transparent px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-gray-100 hover:text-text-main dark:hover:bg-gray-800 transition-colors">
                            Nuevos Leads
                        </button>
                        <button className="rounded-full border border-transparent px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-gray-100 hover:text-text-main dark:hover:bg-gray-800 transition-colors">
                            Clientes VIP
                        </button>
                        <button 
                            onClick={() => queryClient.invalidateQueries({ queryKey: ['contacts'] })}
                            className="ml-auto text-text-secondary hover:text-primary transition-colors flex items-center"
                            title="Actualizar lista"
                        >
                            <span className={`material-symbols-outlined ${isFetching ? 'animate-spin' : ''}`}>refresh</span>
                        </button>
                    </div>

                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-4 border-b border-gray-200 bg-background-light px-6 py-3 text-xs font-semibold text-text-secondary dark:bg-background-dark dark:border-gray-800 shrink-0">
                        <div className="col-span-1 flex items-center">
                            <input className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-700" type="checkbox" />
                        </div>
                        <div className="col-span-4">Nombre y Correo</div>
                        <div className="col-span-3">Teléfono</div>
                        <div className="col-span-2">Etapa</div>
                        <div className="col-span-2 text-right">Última Actividad</div>
                    </div>

                    {/* Table Body (Scrollable) */}
                    <div className="flex-1 overflow-y-auto no-scrollbar">
                        {isLoading ? (
                            <div className="p-8 text-center text-text-secondary dark:text-gray-400">Cargando contactos...</div>
                        ) : contacts.length === 0 ? (
                            <div className="p-8 text-center text-text-secondary dark:text-gray-400">No hay contactos encontrados. ¡Crea el primero!</div>
                        ) : (contacts || []).map((contact) => {
                            const initials = contact.full_name ? contact.full_name.substring(0, 2).toUpperCase() : 'CN';
                            const isActive = contact.id === activeContact?.id;
                            return (
                                <div
                                    key={contact.id}
                                    onClick={() => setActiveContactId(contact.id)}
                                    className={`group grid cursor-pointer grid-cols-12 gap-4 border-b border-gray-100 px-6 py-4 transition-colors dark:border-gray-800 ${isActive
                                        ? 'bg-primary/5 hover:bg-primary/5 dark:bg-primary/10'
                                        : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                                        }`}
                                >
                                    <div className="col-span-1 flex items-center">
                                        <input className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-700" type="checkbox" />
                                    </div>
                                    <div className="col-span-4 flex items-center gap-3">
                                        {contact.avatar ? (
                                            <div
                                                className="h-10 w-10 shrink-0 rounded-full bg-cover bg-center"
                                                style={{ backgroundImage: `url('${resolveMediaUrl(contact.avatar)}')` }}
                                            ></div>
                                        ) : (
                                            <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-sm font-bold bg-primary/10 text-primary`}>
                                                {initials}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold text-text-main dark:text-white">
                                                {contact.full_name || contact.first_name || 'Sin Nombre'}
                                            </p>
                                            <p className="truncate text-xs text-text-secondary">{contact.email}</p>
                                        </div>
                                    </div>
                                    <div className="col-span-3 flex items-center gap-2">
                                        <p className="text-sm text-text-main dark:text-gray-300">{contact.phone || '—'}</p>
                                    </div>
                                    <div className="col-span-2 flex items-center">
                                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
                                            {contact.pipeline_stage || 'Lead'}
                                        </span>
                                    </div>
                                    <div className="col-span-2 flex flex-col justify-center text-right">
                                        <p className="text-xs font-medium text-text-main dark:text-gray-300">
                                            {contact.last_activity_at ? dayjs(contact.last_activity_at).fromNow() : 'Sin actividad'}
                                        </p>
                                        <p className="text-[10px] text-text-secondary">{contact.last_activity_type || 'Creado'}</p>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* Detailed View (Right Panel) */}
                <aside className="hidden xl:flex w-2/5 flex-col border-l border-gray-200 bg-white dark:bg-surface-dark dark:border-gray-800 overflow-y-auto no-scrollbar">
                    {/* Profile Header */}
                    {activeContact ? (
                        <div className="flex flex-col h-full w-full">
                            <div className="p-6 pb-0 shrink-0">
                                <div className="flex items-start justify-between">
                                    <div className="flex gap-4">
                                        {activeContact.avatar ? (
                                            <div
                                                className="h-16 w-16 rounded-xl bg-cover bg-center shadow-sm shrink-0"
                                                style={{ backgroundImage: `url('${resolveMediaUrl(activeContact.avatar)}')` }}
                                            ></div>
                                        ) : (
                                            <div
                                                className="h-16 w-16 rounded-xl bg-cover bg-center shadow-sm shrink-0 flex items-center justify-center bg-primary/10 text-primary text-2xl font-bold"
                                            >
                                                {activeContact.full_name ? activeContact.full_name.substring(0, 2).toUpperCase() : 'CN'}
                                            </div>
                                        )}
                                        <div>
                                            <h2 className="text-xl font-bold text-text-main dark:text-white">{activeContact.full_name || activeContact.first_name || 'Sin Nombre'}</h2>
                                            <p className="text-sm text-text-secondary">{activeContact.company || 'Sin Empresa'}</p>
                                            <div className="mt-2 flex gap-2">
                                                {activeContact.tags?.map((tag, idx) => (
                                                    <span key={idx} className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">#{tag}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Link to={`/contacts/${activeContact.id}/360`} className="bg-primary text-white hover:bg-primary/90 shrink-0 transition-colors shadow-sm p-1.5 rounded-md flex flex-col items-center justify-center min-w-[36px]" title="Vista 360">
                                            <span className="material-symbols-outlined text-sm">360</span>
                                        </Link>
                                        {canEditContact && (
                                            <button onClick={handleEditContact} className="text-text-secondary hover:text-primary shrink-0 transition-colors bg-white border border-gray-200 shadow-sm p-1.5 rounded-md dark:bg-surface-dark dark:border-gray-800 flex items-center justify-center" title="Editar Contacto">
                                                <span className="material-symbols-outlined text-sm">edit</span>
                                            </button>
                                        )}
                                        {canDeleteContact && (
                                            <button onClick={handleDeleteContact} disabled={deleteMutation.isPending} className="text-text-secondary hover:text-red-500 shrink-0 p-1.5 transition-colors bg-white border border-gray-200 shadow-sm rounded-md dark:bg-surface-dark dark:border-gray-800 flex items-center justify-center" title="Eliminar Contacto">
                                                <span className="material-symbols-outlined text-sm">{deleteMutation.isPending ? 'hourglass_empty' : 'delete'}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="mt-6 flex gap-3">
                                    {(() => {
                                        const whatsappConv = (activeContactDetails?.conversations || []).find(c => c.channel === 'whatsapp');
                                        return (
                                            <Link 
                                                to={whatsappConv ? `/chat?conversation=${whatsappConv.id}` : '#'}
                                                className={`flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-primary/90 transition-colors ${!whatsappConv ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                onClick={(e) => !whatsappConv && e.preventDefault()}
                                            >
                                                <span className="material-symbols-outlined text-[18px]">chat</span>
                                                WhatsApp
                                            </Link>
                                        );
                                    })()}
                                    <button 
                                        onClick={() => {
                                            setActiveTab('actividad');
                                            setTimelineFilter('notas');
                                        }}
                                        className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                                            timelineFilter === 'notas' && activeTab === 'actividad'
                                                ? 'bg-primary/10 border-primary text-primary'
                                                : 'border-gray-200 bg-white text-text-main hover:bg-gray-50 dark:bg-surface-dark dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
                                        }`}
                                    >
                                        <span className="material-symbols-outlined text-[18px]">sticky_note_2</span>
                                        Notas
                                    </button>
                                    <button className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-text-main hover:bg-gray-50 dark:bg-surface-dark dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors">
                                        <span className="material-symbols-outlined text-[18px]">mail</span>
                                        Email
                                    </button>
                                </div>

                                {/* Tabs */}
                                <div className="mt-6 flex border-b border-gray-200 dark:border-gray-800">
                                    <button 
                                        onClick={() => setActiveTab('actividad')}
                                        className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'actividad' ? 'border-b-2 border-primary text-primary' : 'border-b-2 border-transparent text-text-secondary hover:text-text-main'}`}
                                    >
                                        Actividad
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('info')}
                                        className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'info' ? 'border-b-2 border-primary text-primary' : 'border-b-2 border-transparent text-text-secondary hover:text-text-main'}`}
                                    >
                                        Info
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('archivos')}
                                        className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'archivos' ? 'border-b-2 border-primary text-primary' : 'border-b-2 border-transparent text-text-secondary hover:text-text-main'}`}
                                    >
                                        Archivos
                                    </button>
                                </div>
                            </div>

                            {/* Scrollable Content Right Panel */}
                            <div className="flex-1 bg-background-light p-6 dark:bg-background-dark/50 space-y-6 overflow-y-auto no-scrollbar">

                                {activeTab === 'actividad' && (
                                    <>
                                        {/* Activity Filters */}
                                        <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar pb-1">
                                            {[
                                                { id: 'all', label: 'Todo', icon: 'list' },
                                                { id: 'notas', label: 'Notas', icon: 'sticky_note_2' },
                                                { id: 'message', label: 'Chats', icon: 'chat' },
                                                { id: 'stage_change', label: 'Etapas', icon: 'rebase_edit' },
                                            ].map(filter => (
                                                <button
                                                    key={filter.id}
                                                    onClick={() => setTimelineFilter(filter.id)}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                                                        timelineFilter === filter.id
                                                            ? 'bg-primary text-white shadow-sm'
                                                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
                                                    }`}
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">{filter.icon}</span>
                                                    {filter.label}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Add Note Widget */}
                                        {canUseNotes && (
                                        <div className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:bg-surface-dark dark:border-gray-800 transition-colors ${createNoteMutation.isPending ? 'opacity-70' : ''}`}>
                                            <textarea
                                                className="w-full resize-none border-none bg-transparent p-0 text-sm placeholder-text-secondary focus:ring-0 text-text-main dark:text-gray-200"
                                                placeholder="Agregar una nota..."
                                                rows={3}
                                                value={noteBody}
                                                onChange={(e) => setNoteBody(e.target.value)}
                                                disabled={createNoteMutation.isPending}
                                            ></textarea>
                                            
                                            {attachments.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-2 py-2 border-t border-gray-50 dark:border-gray-800">
                                                    {(attachments || []).map((file, idx) => (
                                                        <div key={idx} className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded text-[11px] text-gray-600 dark:text-gray-400">
                                                            <span className="material-symbols-outlined text-[14px]">attach_file</span>
                                                            <span className="truncate max-w-[100px]">{file.name}</span>
                                                            <button onClick={() => removeAttachment(idx)} className="hover:text-red-500 ml-1">
                                                                <span className="material-symbols-outlined text-[14px]">close</span>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 dark:border-gray-800">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <input type="file" multiple className="hidden" ref={fileInputRef} onChange={handleFileChange} />
                                                    <button
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className="text-text-secondary hover:text-primary"
                                                        title="Adjuntar"
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">attach_file</span>
                                                    </button>
                                                    <button className="text-text-secondary hover:text-primary" title="Próximamente"><span className="material-symbols-outlined text-[20px]">image</span></button>
                                                    <DictateButton
                                                        onAppend={setNoteBody}
                                                        disabled={createNoteMutation.isPending}
                                                        size={20}
                                                    />
                                                </div>
                                                <button 
                                                    onClick={handleSaveNote}
                                                    disabled={createNoteMutation.isPending || (!noteBody.trim() && attachments.length === 0)}
                                                    className="rounded-md bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition-all flex items-center gap-2"
                                                >
                                                    {createNoteMutation.isPending && <span className="material-symbols-outlined animate-spin text-[14px]">sync</span>}
                                                    Guardar
                                                </button>
                                            </div>
                                        </div>
                                        )}

                                        {/* Timeline */}
                                        <div className="space-y-6">
                                            {isLoadingDetails ? (
                                                <div className="flex justify-center py-8">
                                                    <span className="material-symbols-outlined animate-spin text-primary opacity-50">sync</span>
                                                </div>
                                            ) : (
                                                <>
                                                    {(() => {
                                                        // 1. Consolidate different types of events
                                                        const eventNotes = (activeContactDetails?.notes || []).map(n => ({
                                                            id: `note-${n.id}`,
                                                            noteId: n.id,
                                                            type: 'note',
                                                            title: 'Nota Interna',
                                                            user: n.user?.name || 'Usuario',
                                                            body: n.body,
                                                            created_at: n.created_at,
                                                            color: 'bg-yellow-400'
                                                        }));

                                                        const eventConversations = (activeContactDetails?.conversations || []).flatMap(conv => 
                                                            conv.latest_message ? [{
                                                                id: `msg-${conv.latest_message.id}`,
                                                                conversationId: conv.id,
                                                                type: 'message',
                                                                title: conv.channel === 'whatsapp' ? 'Mensaje de WhatsApp' : 'Mensaje de Instagram',
                                                                body: conv.latest_message.body,
                                                                created_at: conv.latest_message.created_at,
                                                                color: conv.channel === 'whatsapp' ? 'bg-green-500' : 'bg-pink-500',
                                                                isIncoming: conv.latest_message.direction === 'inbound'
                                                            }] : []
                                                        );

                                                        const eventStageChanges = (activeContactDetails?.activities || [])
                                                            .filter(a => a.type === 'stage_change')
                                                            .map(a => ({
                                                                id: `activity-${a.id}`,
                                                                type: 'stage_change',
                                                                title: 'Cambio de Etapa',
                                                                body: a.description,
                                                                created_at: a.created_at,
                                                                color: 'bg-blue-500'
                                                            }));

                                                        // 2. Filter and Sort
                                                        let filteredActivities = [...eventNotes, ...eventConversations, ...eventStageChanges]
                                                            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

                                                        if (timelineFilter !== 'all') {
                                                            const typeMap = {
                                                                'notas': 'note',
                                                                'message': 'message',
                                                                'stage_change': 'stage_change'
                                                            };
                                                            filteredActivities = filteredActivities.filter(item => item.type === typeMap[timelineFilter]);
                                                        }

                                                        // 3. Take top items (increased limit)
                                                        const topActivities = filteredActivities.slice(0, 20);

                                                        if (topActivities.length === 0) {
                                                            return (
                                                                <div className="py-8 text-center text-text-secondary text-sm">
                                                                    {timelineFilter === 'notas' ? 'No hay notas aún' : 'Sin actividad reciente'}
                                                                </div>
                                                            );
                                                        }

                                                        return topActivities.map(item => (
                                                            <div key={item.id} className="relative pl-6">
                                                                <div className="absolute left-0 top-1 h-full w-px bg-gray-200 dark:bg-gray-700"></div>
                                                                <div className={`absolute -left-1.5 top-1.5 h-3 w-3 rounded-full border-2 border-white dark:border-surface-dark ${item.color}`}></div>
                                                                <div className="mb-4 group">
                                                                    <div className="flex items-center justify-between">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-sm font-semibold text-text-main dark:text-white truncate">{item.title}</span>
                                                                            <span className="text-[10px] text-text-secondary">{dayjs(item.created_at).fromNow()}</span>
                                                                        </div>
                                                                        {item.type === 'note' && (
                                                                            <button 
                                                                                onClick={(e) => handleDeleteClick(item.noteId, e)}
                                                                                className="opacity-0 group-hover:opacity-100 text-text-secondary hover:text-red-500 transition-all"
                                                                            >
                                                                                <span className="material-symbols-outlined text-[14px]">delete</span>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                    <div className={`mt-2 rounded-lg p-3 text-sm text-text-main shadow-sm dark:text-gray-200 ${
                                                                        item.type === 'note' 
                                                                            ? 'bg-yellow-50/50 dark:bg-yellow-900/10 border border-yellow-100/50' 
                                                                            : 'bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800'
                                                                    }`}>
                                                                        {item.type === 'note' ? (
                                                                            <ReactMarkdown 
                                                                                components={{
                                                                                    p: ({node, ...props}) => <p className="mb-1 last:mb-0" {...props} />,
                                                                                    ul: ({node, ...props}) => <ul className="list-disc ml-4 mb-1" {...props} />,
                                                                                    ol: ({node, ...props}) => <ol className="list-decimal ml-4 mb-1" {...props} />,
                                                                                }}
                                                                            >
                                                                                {item.body}
                                                                            </ReactMarkdown>
                                                                        ) : (
                                                                            <Link 
                                                                                to={`/chat?conversation=${item.conversationId}`}
                                                                                className="block hover:opacity-70 transition-opacity"
                                                                                title="Ir a la conversación"
                                                                            >
                                                                                <p>{item.body}</p>
                                                                                <div className="mt-2 flex items-center gap-1 text-[10px] font-bold text-primary">
                                                                                    <span className="material-symbols-outlined text-[12px]">open_in_new</span>
                                                                                    VER CHAT
                                                                                </div>
                                                                            </Link>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ));
                                                    })()}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}

                                {activeTab === 'info' && (
                                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                        <div className="rounded-xl border border-gray-200 bg-white dark:bg-surface-dark dark:border-gray-800 p-4 shadow-sm">
                                            <h4 className="text-xs font-bold text-primary uppercase tracking-wider mb-4 flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[16px]">info</span>
                                                Información Básica
                                            </h4>
                                            <div className="grid grid-cols-1 gap-y-4">
                                                {[
                                                    { label: 'EMAIL', value: activeContact.email, icon: 'mail' },
                                                    { label: 'TELÉFONO', value: activeContact.phone, icon: 'call' },
                                                    { label: 'EMPRESA', value: activeContact.company, icon: 'business' },
                                                    { label: 'SEGMENTACIÓN', value: activeContact.segmentation, icon: 'sell' },
                                                    { label: 'DIRECCIÓN', value: activeContact.address, icon: 'location_on' },
                                                    { label: 'CARGO', value: activeContact.position, icon: 'badge' },
                                                    { label: 'INDUSTRIA', value: activeContact.industry, icon: 'factory' },
                                                    { label: 'TERRITORIO', value: activeContact.territory, icon: 'map' },
                                                    { label: 'FUENTE', value: activeContact.source, icon: 'hub' },
                                                ].map((field, idx) => (
                                                    <div key={idx} className="flex flex-col">
                                                        <span className="text-[10px] font-bold text-text-secondary uppercase">{field.label}</span>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className="material-symbols-outlined text-sm text-gray-400">{field.icon}</span>
                                                            <span className="text-sm text-text-main dark:text-gray-200">{field.value || '—'}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Custom Fields if any */}
                                        {(activeContact.custom_field_values || []).length > 0 && (
                                            <div className="rounded-xl border border-gray-200 bg-white dark:bg-surface-dark dark:border-gray-800 p-4 shadow-sm">
                                                <h4 className="text-xs font-bold text-primary uppercase tracking-wider mb-4 flex items-center gap-2">
                                                    <span className="material-symbols-outlined text-[16px]">widgets</span>
                                                    Campos Personalizados
                                                </h4>
                                                <div className="grid grid-cols-1 gap-y-4">
                                                    {(activeContact.custom_field_values || []).map((v, idx) => (
                                                        <div key={idx} className="flex flex-col">
                                                            <span className="text-[10px] font-bold text-text-secondary uppercase">{v.field?.name || 'Campo'}</span>
                                                            <span className="text-sm text-text-main dark:text-gray-200 mt-1">{v.value || '—'}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'archivos' && (
                                    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                        {(() => {
                                            const noteFiles = (activeContactDetails?.notes || []).flatMap(n => 
                                                (n.attachments || []).map(file => ({
                                                    id: `file-note-${file.path}`,
                                                    name: file.name,
                                                    path: file.path, // Full path usually stored in DB or relative to storage
                                                    type: 'attachment',
                                                    date: n.created_at,
                                                    source: 'Nota Interna',
                                                    icon: 'description'
                                                }))
                                            );

                                            const mediaMessages = (activeContactDetails?.media_messages || []).map(m => ({
                                                id: `file-msg-${m.id}`,
                                                name: m.media_url?.split('/').pop() || 'Archivo',
                                                path: m.media_url,
                                                type: m.type,
                                                date: m.created_at,
                                                source: m.conversation?.channel === 'whatsapp' ? 'WhatsApp' : 'Instagram',
                                                icon: m.type === 'image' ? 'image' : m.type === 'video' ? 'videocam' : 'attach_file'
                                            }));

                                            const allFiles = [...noteFiles, ...mediaMessages].sort((a,b) => new Date(b.date) - new Date(a.date));

                                            if (allFiles.length === 0) {
                                                return (
                                                    <div className="flex flex-col items-center justify-center py-12 text-center opacity-40">
                                                        <span className="material-symbols-outlined text-4xl mb-2">folder_off</span>
                                                        <p className="text-sm">Sin archivos compartidos</p>
                                                    </div>
                                                );
                                            }

                                            return allFiles.map(file => (
                                                <div key={file.id} className="group flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white hover:border-primary/30 dark:bg-surface-dark dark:border-gray-800 transition-all shadow-sm">
                                                    <div className="h-10 w-10 rounded-lg bg-gray-50 flex items-center justify-center text-primary dark:bg-gray-800 shrink-0">
                                                        <span className="material-symbols-outlined">{file.icon}</span>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-bold text-text-main dark:text-gray-200 truncate">{file.name}</p>
                                                        <p className="text-[10px] text-text-secondary mt-0.5">{file.source} • {dayjs(file.date).format('D MMM, HH:mm')}</p>
                                                    </div>
                                                    <a 
                                                        href={file.path.startsWith('http') ? file.path : `${import.meta.env.VITE_API_URL}${file.path}`} 
                                                        target="_blank" 
                                                        rel="noreferrer"
                                                        className="text-text-secondary hover:text-primary transition-colors pr-1"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                                                    </a>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                            <span className="material-symbols-outlined text-6xl text-gray-200 dark:text-gray-800 mb-4">account_circle</span>
                            <h3 className="text-xl font-bold text-text-main dark:text-gray-300">Ningún contacto seleccionado</h3>
                            <p className="text-sm text-text-secondary mt-2 max-w-sm">
                                Selecciona un contacto de la lista a la izquierda para ver sus detalles y su historial de conversaciones.
                            </p>
                        </div>
                    )}
                </aside>
            </div>

            <ContactModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                contact={selectedContact}
            />

            {/* Custom Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setIsDeleteModalOpen(false)}></div>
                    <div className="relative bg-white dark:bg-[#1a100c] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 text-center">
                            <div className="size-16 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto mb-4">
                                <span className="material-symbols-outlined text-3xl">delete_forever</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">¿Eliminar actividad?</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                Esta acción eliminará permanentemente la nota y todos sus archivos adjuntos. No se puede deshacer.
                            </p>
                        </div>
                        <div className="flex border-t border-gray-100 dark:border-gray-800">
                            <button 
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="flex-1 px-4 py-4 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={handleConfirmDelete}
                                disabled={deleteNoteMutation.isPending}
                                className="flex-1 px-4 py-4 text-sm font-bold text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                            >
                                {deleteNoteMutation.isPending && <span className="material-symbols-outlined animate-spin text-sm">sync</span>}
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
